import { t } from '../i18n';

type StatsData = {
    n: number;
    totalCount: number;
    avg: number | null;
    median: number | null;
    min: number | null;
    max: number | null;
    dist: [string, number][];
    consensus: string | null;
};

type Props = {
    stats: StatsData;
};

function getFormattedNumberOrDash(value: number | null): string {
    if (value === null || value === undefined) return '-';
    if (Number.isInteger(value)) return value.toString();
    return value.toFixed(1);
}

export default function Statistics({stats}: Readonly<Props>) {
    return (
        <div className="stats-container rounded-lg border shadow-sm p-4 bg-white">
            <div className="flex items-center justify-between mb-3">
                <div className="label label-on-light">{t('stats.title')}</div>
                {stats.consensus && (
                    <div className="text-sm font-medium text-indigo-700">
                        {t('stats.consensus')}: <span
                        className="inline-block px-2 py-0.5 rounded-lg border border-indigo-200 bg-indigo-50">
                            {stats.consensus}
                        </span>
                    </div>
                )}
            </div>

            {/* Vote Distribution - Prominent Display */}
            {stats.dist.length > 0 && (
                <div className="mb-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <h3 className="text-sm font-semibold text-slate-700 mb-3">{t('stats.distribution')}</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                        {stats.dist.map(([value, count]) => (
                            <div
                                key={value}
                                className="flex flex-col items-center justify-center p-3 bg-white rounded-lg border-2 border-slate-200 shadow-sm"
                            >
                                <div className="text-2xl font-bold text-slate-700">{value}</div>
                                <div className="text-xs text-slate-500 mt-1">
                                    {count === 1 ? t('stats.vote.one', { count }) : t('stats.vote.other', { count })}
                                </div>
                                {/* Visual bar indicator */}
                                <div className="w-full mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-indigo-500 rounded-full transition-all"
                                        style={{width: `${(count / stats.totalCount) * 100}%`}}
                                    ></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Numeric Statistics */}
            {stats.n > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="text-center">
                        <div className="text-xs text-slate-500">{t('stats.avg')}</div>
                        <div className="text-xl font-semibold">{getFormattedNumberOrDash(stats.avg)}</div>
                    </div>
                    <div className="text-center">
                        <div className="text-xs text-slate-500">{t('stats.median')}</div>
                        <div className="text-xl font-semibold">{getFormattedNumberOrDash(stats.median)}</div>
                    </div>
                    <div className="text-center">
                        <div className="text-xs text-slate-500">{t('stats.min')}</div>
                        <div className="text-xl font-semibold">{getFormattedNumberOrDash(stats.min)}</div>
                    </div>
                    <div className="text-center">
                        <div className="text-xs text-slate-500">{t('stats.max')}</div>
                        <div className="text-xl font-semibold">{getFormattedNumberOrDash(stats.max)}</div>
                    </div>
                </div>
            )}
        </div>
    );
}
